import React from 'react'
import {
    HiOutlineCube, HiOutlineDotsCircleHorizontal, HiOutlineGlobe, HiOutlineHome, HiOutlineIdentification,
    HiOutlineLightBulb, HiOutlineOfficeBuilding, HiOutlineScissors, HiOutlineShoppingBag, HiOutlineSpeakerphone,
    HiOutlineTruck, HiOutlineUserGroup, HiOutlineCog
} from 'react-icons/hi'

// Labels for the backend's expense codes (ExpenseCategory, ExpenseUnit, ExpensePaidBy enums).
export const EXPENSE_GROUPS = [
    { key: 'stock', label: 'Stock' },
    { key: 'people', label: 'People' },
    { key: 'place', label: 'Place' },
    { key: 'running', label: 'Running the business' }
]

export const EXPENSE_CATEGORIES = [
    { key: 'RAW_MATERIALS', label: 'Raw materials', hint: 'Fabric, thread, buttons, trims', group: 'stock', icon: <HiOutlineScissors /> },
    { key: 'BRAND_STOCK', label: 'Stock from brands', hint: 'Ready-made products to resell', group: 'stock', icon: <HiOutlineShoppingBag /> },
    { key: 'PACKAGING', label: 'Packaging', hint: 'Boxes, mailers, tags', group: 'stock', icon: <HiOutlineCube /> },
    { key: 'TAILORING_WAGES', label: 'Tailoring wages', hint: 'Tailors and helpers', group: 'people', icon: <HiOutlineUserGroup /> },
    { key: 'SALARIES', label: 'Salaries', hint: 'Shop and office staff', group: 'people', icon: <HiOutlineIdentification /> },
    { key: 'RENT', label: 'Rent', hint: 'Tailoring unit, shop', group: 'place', icon: <HiOutlineHome /> },
    { key: 'UTILITIES', label: 'Utilities', hint: 'Power, water, internet', group: 'place', icon: <HiOutlineLightBulb /> },
    { key: 'MAINTENANCE', label: 'Maintenance & repairs', hint: 'Machines, fittings', group: 'place', icon: <HiOutlineCog /> },
    { key: 'TRANSPORT', label: 'Transport & delivery', hint: 'Courier, travel, fuel', group: 'running', icon: <HiOutlineTruck /> },
    { key: 'MARKETING', label: 'Marketing', hint: 'Ads, photos, printing', group: 'running', icon: <HiOutlineSpeakerphone /> },
    { key: 'OTHER', label: 'Other', hint: 'Anything else', group: 'running', icon: <HiOutlineDotsCircleHorizontal /> }
]

export const EXPENSE_UNITS = [
    { key: 'TAILORING_UNIT', label: 'Tailoring unit', icon: <HiOutlineScissors /> },
    { key: 'SHOP', label: 'Shop', icon: <HiOutlineOfficeBuilding /> },
    { key: 'ONLINE', label: 'Online & general', icon: <HiOutlineGlobe /> }
]

export const PAID_BY = [
    { key: 'CASH', label: 'Cash' },
    { key: 'UPI', label: 'UPI' },
    { key: 'BANK', label: 'Bank transfer' },
    { key: 'CARD', label: 'Card' },
    { key: 'OTHER', label: 'Other' }
]

const byKey = (list) => Object.fromEntries(list.map(x => [x.key, x]))
const CATEGORY_BY_KEY = byKey(EXPENSE_CATEGORIES)
const UNIT_BY_KEY = byKey(EXPENSE_UNITS)
const PAID_BY_KEY = byKey(PAID_BY)

export const categoryOf = (key) => CATEGORY_BY_KEY[key] || CATEGORY_BY_KEY.OTHER
export const unitLabel = (key) => (UNIT_BY_KEY[key] ? UNIT_BY_KEY[key].label : key || '')
export const paidByLabel = (key) => (PAID_BY_KEY[key] ? PAID_BY_KEY[key].label : '')

// ---- dates (shop days are Indian dates; plain YYYY-MM-DD, no timezone shifts) ----

const pad = (n) => String(n).padStart(2, '0')
export const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const parseIso = (iso) => {
    const [y, m, d] = String(iso).split('-').map(Number)
    return new Date(y, (m || 1) - 1, d || 1)
}
export const todayIso = () => isoDate(new Date())

// same day next month (31 Jan -> 28/29 Feb), never after today
export const nextMonthIso = (iso) => {
    const d = parseIso(iso)
    const target = new Date(d.getFullYear(), d.getMonth() + 1, 1)
    const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
    target.setDate(Math.min(d.getDate(), last))
    const next = isoDate(target)
    return next > todayIso() ? todayIso() : next
}

const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
const monthEnd = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0)

// Indian financial year: 1 April - 31 March
export const PERIODS = [
    { key: 'month', label: 'This month', range: (t) => [monthStart(t), monthEnd(t)] },
    { key: 'last', label: 'Last month', range: (t) => [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)] },
    { key: '3m', label: 'Last 3 months', range: (t) => [new Date(t.getFullYear(), t.getMonth() - 2, 1), monthEnd(t)] },
    {
        key: 'fy', label: 'This financial year', range: (t) => {
            const startYear = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1
            return [new Date(startYear, 3, 1), new Date(startYear + 1, 2, 31)]
        }
    }
]

export const periodRange = (key) => {
    const p = PERIODS.find(x => x.key === key) || PERIODS[0]
    const [from, to] = p.range(new Date())
    return { from: isoDate(from), to: isoDate(to) }
}

// which preset a from/to pair is, or 'custom'
export const periodKeyOf = (from, to) => {
    const hit = PERIODS.find(p => {
        const r = periodRange(p.key)
        return r.from === from && r.to === to
    })
    return hit ? hit.key : 'custom'
}

export const formatDay = (iso, opts = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) =>
    parseIso(iso).toLocaleDateString('en-IN', opts)

export const formatMonth = (yearMonth, long = false) => {
    const [y, m] = String(yearMonth).split('-').map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString('en-IN', long ? { month: 'long', year: 'numeric' } : { month: 'short' })
}
